import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import VideoButtonItem from '../VideoButtonItem';
import {
  addAlphaComponent,
  book,
  heavy,
  regular,
  semiBold,
  flexCenter,
  colors,
  resetButton,
} from '@/styles';

/**
 * VideoEndScreen component
 *
 * @param {import('./VideoEndScreen').VideoEndScreenProps}
 */
export const VideoEndScreen = ({
  className,
  buttons,
  endScreen,
  player,
  hasCta,
  isTouch,
  isRebrand,
}) => {
  const [size, setSize] = useState('medium');

  const endScreenNode = useRef();

  const sizeDidUpdate = () => {
    let endScreenNodeOffsetWidth = null;
    const width = endScreenNode.current.offsetWidth;

    if (width > 600) {
      endScreenNodeOffsetWidth = 'medium';
    } else if (width > 420) {
      endScreenNodeOffsetWidth = 'small';
    } else {
      endScreenNodeOffsetWidth = 'mobile';
    }

    if (size !== endScreenNodeOffsetWidth) {
      setSize(endScreenNodeOffsetWidth);
    }
  };

  useEffect(() => {
    sizeDidUpdate();
  }, []);

  useEffect(() => {
    sizeDidUpdate();
  }, [size]);

  return (
    <div
      className={classNames(className, size, { hasEndScreen: endScreen, hasCta, isTouch })}
      ref={endScreenNode}
    >
      {buttons &&
        buttons.map((button) => {
          const { text, element, callback, name } = button;
          return (
            <VideoButtonItem
              key={name}
              className={size}
              SvgElement={element}
              name={name}
              handleClick={() => callback(player)}
              text={text}
              isRebrand={isRebrand}
            />
          );
        })}
      {endScreen && <div className="dynamic-end-screen-content">{endScreen}</div>}
    </div>
  );
};

VideoEndScreen.propTypes = {
  /** Emotion base style */
  className: PropTypes.string,
  /** The vjs player */
  player: PropTypes.shape().isRequired,
  /** A list of button objects to display on the end screen */
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      callback: PropTypes.func.isRequired,
      element: PropTypes.elementType.isRequired,
      name: PropTypes.string,
    }).isRequired,
  ),
  endScreen: PropTypes.node,
  hasCta: PropTypes.bool,
  isTouch: PropTypes.bool,
  isRebrand: PropTypes.bool.isRequired,
};

VideoEndScreen.defaultProps = {
  className: '',
  buttons: null,
  endScreen: null,
  hasCta: false,
  isTouch: false,
};

const styledVideoEndScreen = styled(VideoEndScreen)`
  ${flexCenter()}
  height: 100%;
  background-color: ${addAlphaComponent(colors.almostBlack, 0.9)};

  &.hasEndScreen {
    /* account for play controls at the bottom */
    padding-bottom: 3.8rem;
  }

  &.hasEndScreen.isTouch {
    /* account for play rate buttons on top of the screen */
    padding-top: 2.5rem;
  }

  &.hasEndScreen.hasCta {
    /* additional bump when a cta is present */
    padding-top: 4rem;
  }

  ${(props) =>
    props.buttons &&
    `button {
       ${({ isRebrand }) => (isRebrand ? regular() : book())}
       ${resetButton()}
       font-size: 0.8rem;
       margin: 2rem;
       color: ${colors.white};
       display: flex;
       flex-direction: column;
       align-items: center;
     }`}

  .circle {
    ${flexCenter()}
    border-radius: 50%;
    background-color: ${colors.white};
    width: 4.7rem;
    height: 4.7rem;
    margin-bottom: 0.7em;

    svg {
      height: 2.5rem;
    }
  }

  .dynamic-end-screen-content {
    height: 100%;
    overflow: hidden;
    &.mobile {
      transform: scale(0.6);
    }
    &.tiny {
      transform: scale(0.6);
    }
  }

  .like {
    margin-top: 3%;
  }

  .reply {
    margin-left: 3%;
  }

  span {
    ${({ isRebrand }) => (isRebrand ? semiBold() : heavy())}
  }

  /* Breakpoints: Mobile  */
  > .mobile {
    transform: scale(0.6);
    margin: -0.6rem; /* A fake margin will appear due to scale diff */

    button {
      font-size: 1rem;
    }
  }

  > .small {
    transform: scale(0.7);
    margin: 0; /* A fake margin will appear due to scale diff */
  }
`;

export default styledVideoEndScreen;
